import React from 'react';
import { Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface ChangeRequest {
  id: string;
  status: 'pending' | 'approved' | 'denied';
  requested_clock_in?: string;
  requested_clock_out?: string;
  requested_total_minutes?: number;
  request_reason?: string;
  requested_at: string;
}

interface ChangeRequestBadgeProps {
  request: ChangeRequest;
  originalEntry: {
    clock_in?: string;
    clock_out?: string;
    total_minutes?: number;
  };
}

export default function ChangeRequestBadge({ request, originalEntry }: ChangeRequestBadgeProps) {
  // Calculate time difference
  const getTimeDifference = () => {
    const originalMinutes = originalEntry.total_minutes || 0;
    const requestedMinutes = request.requested_total_minutes || 0;
    const difference = requestedMinutes - originalMinutes;
    
    if (difference === 0) return null;
    
    const hours = Math.floor(Math.abs(difference) / 60);
    const minutes = Math.abs(difference) % 60;
    const sign = difference > 0 ? '+' : '-';
    
    return `${sign}${hours}h ${minutes}m`;
  };

  const timeDiff = getTimeDifference();

  return (
    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
              Change Requested
            </span>
            <span className="text-xs text-amber-600">
              {format(new Date(request.requested_at), 'MMM d, h:mm a')}
            </span>
          </div>
          
          {/* Time change summary */}
          <div className="space-y-1 text-xs text-amber-700">
            {request.requested_clock_in && request.requested_clock_out && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>
                  {format(new Date(request.requested_clock_in), 'h:mm a')} - {format(new Date(request.requested_clock_out), 'h:mm a')}
                </span>
                {timeDiff && (
                  <span className={`font-medium ${timeDiff.startsWith('+') ? 'text-green-700' : 'text-red-700'}`}>
                    ({timeDiff})
                  </span>
                )}
              </div>
            )}
            
            {request.request_reason && (
              <div className="text-amber-600 italic truncate" title={request.request_reason}>
                "{request.request_reason}"
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}